import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardBody, 
  Spinner, 
  Alert,
  Button,
  Badge
} from 'reactstrap';
import axiosInstance from '../../api/Axios';

const StudentDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state?.booking;
  
  const [fullStudentDetails, setFullStudentDetails] = useState(null);
  const [academicRecords, setAcademicRecords] = useState([]);
  const [loadingStudentDetails, setLoadingStudentDetails] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxDocType, setLightboxDocType] = useState('image');
  
  // Backend base URL for media files
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  // Helper function to determine file type
  const getFileType = (url) => {
    if (!url) return 'document';
    
    const extension = url.split('.').pop().toLowerCase();
    
    // PDF files
    if (['pdf'].includes(extension)) return 'pdf';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
    
    // Other document types (Word, Excel, PowerPoint, etc.)
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) return 'document';
    
    // Default to document for unknown types
    return 'document';
  };

  useEffect(() => {
    if (!booking) {
      navigate('/provider-dashboard/bookings');
      return;
    }
    
    fetchStudentDetails();
  }, [booking, navigate]);

  const fetchStudentDetails = async () => {
    setLoadingStudentDetails(true);
    
    try {
      // Fetch full student details with documents
      const studentsResponse = await axiosInstance.get('/accounts/students-with-docs/');
      
      // Find the student matching this booking's email
      const student = studentsResponse.data.find(
        s => s.user?.email === booking.student_email
      );
      
      if (student) {
        setFullStudentDetails(student);
        
        // Fetch academic records
        if (student.user?.id) {
          const academicResponse = await axiosInstance.get(`/accounts/academic-records/user/${student.user.id}/`);
          setAcademicRecords(academicResponse.data);
        }
      }
    } catch (err) {
      console.error('Error fetching student details:', err);
    } finally {
      setLoadingStudentDetails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'warning', text: 'Pending Approval' },
      'approved': { color: 'success', text: 'Approved' },
      'rejected': { color: 'danger', text: 'Rejected' },
      'cancelled': { color: 'secondary', text: 'Cancelled' },
      'completed': { color: 'info', text: 'Completed' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  if (!booking) {
    return null;
  }

  return (
    <div className="container-fluid py-4">
      {/* Header with back button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Student Details</h2>
        <Button color="secondary" onClick={() => navigate('/provider-dashboard/bookings')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to Bookings
        </Button>
      </div>

      {loadingStudentDetails ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3">Loading student details...</p>
        </div>
      ) : fullStudentDetails ? (
        <div className="row">
          <div className="col-12">
            {/* Student Personal Information */}
            <Card className="mb-4">
              <CardBody>
                <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #007bff", paddingBottom: "5px" }}>
                  Personal Information
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
                  <div>
                    <strong>ID:</strong> {fullStudentDetails.id}
                  </div>
                  <div>
                    <strong>Email:</strong> {fullStudentDetails.user?.email || 'N/A'}
                  </div>
                  <div>
                    <strong>Title:</strong> {fullStudentDetails.title || 'N/A'}
                  </div>
                  <div>
                    <strong>Surname:</strong> {fullStudentDetails.surname}
                  </div>
                  <div>
                    <strong>Other Names:</strong> {fullStudentDetails.other_names}
                  </div>
                  <div>
                    <strong>Gender:</strong> {fullStudentDetails.gender || 'N/A'}
                  </div>
                  <div>
                    <strong>ID Type:</strong> {fullStudentDetails.id_document_type || 'N/A'}
                  </div>
                  <div>
                    <strong>ID Number:</strong> {fullStudentDetails.id_number || 'N/A'}
                  </div>
                  <div>
                    <strong>Phone:</strong> {fullStudentDetails.phone_number || 'N/A'}
                  </div>
                  <div>
                    <strong>Citizenship:</strong> {fullStudentDetails.citizenship || 'N/A'}
                  </div>
                  <div>
                    <strong>Student Number:</strong> {fullStudentDetails.student_number || 'N/A'}
                  </div>
                  <div>
                    <strong>Year of Study:</strong> {fullStudentDetails.year_of_study || 'N/A'}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Address Information */}
            <Card className="mb-4">
              <CardBody>
                <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #28a745", paddingBottom: "5px" }}>
                  Address Information
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <strong>Address Lines:</strong> 
                    <div style={{ 
                      marginTop: "5px", 
                      padding: "8px", 
                      backgroundColor: "#f8f9fa", 
                      borderRadius: "4px", 
                      border: "1px solid #dee2e6",
                      whiteSpace: "pre-wrap" 
                    }}>
                      {fullStudentDetails.addresslines || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <strong>Suburb:</strong> {fullStudentDetails.suburb || 'N/A'}
                  </div>
                  <div>
                    <strong>City:</strong> {fullStudentDetails.city || 'N/A'}
                  </div>
                  <div>
                    <strong>Province:</strong> {fullStudentDetails.province || 'N/A'}
                  </div>
                  <div>
                    <strong>Post Code:</strong> {fullStudentDetails.postcode || 'N/A'}
                  </div>
                  <div>
                    <strong>Country:</strong> {fullStudentDetails.country || 'N/A'}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Academic Information */}
            <Card className="mb-4">
              <CardBody>
                <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #ffc107", paddingBottom: "5px" }}>
                  Academic Information
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
                  <div>
                    <strong>Institution:</strong> {fullStudentDetails.institution_name || 'N/A'}
                  </div>
                  <div>
                    <strong>Campus:</strong> {fullStudentDetails.campus?.name || 'N/A'}
                  </div>
                  <div>
                    <strong>Course:</strong> {fullStudentDetails.course || 'N/A'}
                  </div>
                  <div>
                    <strong>Study Level:</strong> {fullStudentDetails.study_level || 'N/A'}
                  </div>
                  <div>
                    <strong>Year of Registration:</strong> {fullStudentDetails.year_of_registration || 'N/A'}
                  </div>
                  <div>
                    <strong>Enrollment Status:</strong> {fullStudentDetails.enrollment_status || 'N/A'}
                  </div>
                  <div>
                    <strong>Registration Status:</strong> {fullStudentDetails.reg_status || 'N/A'}
                  </div>
                  <div>
                    <strong>Payment Method:</strong> {fullStudentDetails.payment_method || 'N/A'}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Uploaded Documents */}
            {fullStudentDetails.documents && fullStudentDetails.documents.length > 0 && (
              <Card className="mb-4">
                <CardBody>
                  <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #17a2b8", paddingBottom: "5px" }}>
                    Uploaded Documents
                  </h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                    {fullStudentDetails.documents.map(doc => {
                      const documentUrl = doc.url.startsWith('/') ? 
                        `${BACKEND_URL}${doc.url}` : 
                        `${BACKEND_URL}/${doc.url}`;
                      const fileType = getFileType(doc.url);
                      
                      return (
                        <button
                          key={doc.id}
                          onClick={() => {
                            setLightboxImg(documentUrl);
                            setLightboxDocType(fileType);
                          }}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {fileType === 'pdf' ? (
                            <div
                              style={{
                                width: 80,
                                height: 80,
                                backgroundColor: '#e74c3c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "2px solid #ccc",
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              PDF
                            </div>
                          ) : fileType === 'document' ? (
                            <div
                              style={{
                                width: 80,
                                height: 80,
                                backgroundColor: '#3498db',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "2px solid #ccc",
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              DOC
                            </div>
                          ) : (
                            <img
                              src={documentUrl}
                              alt="doc"
                              style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4, border: "2px solid #ccc" }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Academic Records */}
            <Card className="mb-4">
              <CardBody>
                <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #6f42c1", paddingBottom: "5px" }}>
                  Academic Records
                </h3>
                {academicRecords.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Institution</th>
                          <th>Campus</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Year of Study</th>
                          <th>Study Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {academicRecords.map((record, index) => (
                          <tr key={index}>
                            <td>{record.institution_name || 'N/A'}</td>
                            <td>{record.campus_name || 'N/A'}</td>
                            <td>{formatDate(record.start_date)}</td>
                            <td>{formatDate(record.end_date)}</td>
                            <td>{record.year_of_study || 'N/A'}</td>
                            <td>{record.study_level || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Alert color="info">
                    <i className="bi bi-info-circle me-2"></i>
                    No academic records found for this student.
                  </Alert>
                )}
              </CardBody>
            </Card>

            {/* Current Booking Information */}
            <Card className="mb-4">
              <CardBody>
                <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #fd7e14", paddingBottom: "5px" }}>
                  Current Booking Information
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px", padding: "15px", backgroundColor: "#fff3cd", borderRadius: "4px", border: "1px solid #ffc107" }}>
                  <div>
                    <strong>Booking ID:</strong> #{booking.id}
                  </div>
                  <div>
                    <strong>Status:</strong> {getStatusBadge(booking.booking_status)}
                  </div>
                  <div>
                    <strong>Property:</strong> {booking.property_name}
                  </div>
                  <div>
                    <strong>Voucher Code:</strong> <code>{booking.voucher_code}</code>
                  </div>
                  <div>
                    <strong>Check-in:</strong> {formatDate(booking.check_in_date)}
                  </div>
                  <div>
                    <strong>Check-out:</strong> {formatDate(booking.check_out_date)}
                  </div>
                  <div>
                    <strong>Voucher Amount:</strong> {formatCurrency(booking.voucher_amount || 0)}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Status Information */}
            <Card className="mb-4">
              <CardBody>
                <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #dc3545", paddingBottom: "5px" }}>
                  Status Information
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
                  <div>
                    <strong>Document Verified:</strong> 
                    <span style={{ 
                      color: fullStudentDetails.document_verified ? '#28a745' : '#dc3545',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {fullStudentDetails.document_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <strong>Policy Agreed:</strong> 
                    <span style={{ 
                      color: fullStudentDetails.policy_agreed ? '#28a745' : '#dc3545',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {fullStudentDetails.policy_agreed ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <strong>Wheelchair Required:</strong> 
                    <span style={{ 
                      color: fullStudentDetails.wheel_chair_req ? '#ffc107' : '#6c757d',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {fullStudentDetails.wheel_chair_req ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      ) : (
        <Alert color="warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Student details not found or could not be loaded.
        </Alert>
      )}

      {/* Document Lightbox */}
      {lightboxImg && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.9)",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setLightboxImg(null);
              setLightboxDocType('image');
            }
          }}
        >
          {lightboxDocType === 'pdf' ? (
            <div
              style={{
                width: "80vw",
                height: "70vh",
                boxShadow: "0 0 30px #000",
                borderRadius: 8,
                background: "#fff",
                marginBottom: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px"
              }}
            >
              <div style={{ fontSize: "48px", color: "#e74c3c", marginBottom: "20px" }}>
                📄
              </div>
              <h3 style={{ color: "#333", marginBottom: "10px" }}>PDF Document</h3>
              <p style={{ color: "#666", textAlign: "center", marginBottom: "20px" }}>
                Click the button below to open the PDF document in a new tab.
              </p>
              <a
                href={lightboxImg}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "#e74c3c",
                  color: "white",
                  padding: "15px 30px",
                  textDecoration: "none",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                📖 Open PDF in New Tab
              </a>
            </div>
          ) : lightboxDocType === 'document' ? (
            <div
              style={{
                width: "80vw",
                height: "70vh",
                boxShadow: "0 0 30px #000",
                borderRadius: 8,
                background: "#fff",
                marginBottom: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px"
              }}
            >
              <div style={{ fontSize: "48px", color: "#3498db", marginBottom: "20px" }}>
                📄
              </div>
              <h3 style={{ color: "#333", marginBottom: "10px" }}>Document Viewer</h3>
              <p style={{ color: "#666", textAlign: "center", marginBottom: "20px" }}>
                This document format cannot be previewed directly in the browser.
              </p>
              <a
                href={lightboxImg}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "#3498db",
                  color: "white",
                  padding: "10px 20px",
                  textDecoration: "none",
                  borderRadius: "4px",
                  fontWeight: "bold"
                }}
              >
                Download & View Document
              </a>
            </div>
          ) : (
            <img
              src={lightboxImg}
              alt="Full Document"
              style={{
                maxWidth: "80vw",
                maxHeight: "70vh",
                boxShadow: "0 0 30px #000",
                borderRadius: 8,
                background: "#fff",
                marginBottom: "20px"
              }}
              onError={(e) => {
                console.error("Image failed to load:", e);
              }}
            />
          )}
          
          <button
            onClick={() => {
              setLightboxImg(null);
              setLightboxDocType('image');
            }}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentDetailsPage;
